import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../state/store";
import {
  setUsersRefresh,
  pushCreatedTableRowsIds,
  setMarketsError,
  setMarketsLoading,
  resetMarkets
} from "../../state/ducks";
import { selectAppTypesGroups } from "../../state/selectors";

import { alignItems, appTypesNames, routePaths, strCode } from "../../consts";
import {
  adminUserService,
  authService,
  groupsService,
  log,
  marketsService,
  notificationService,
  t,
  usersService
} from "../../services";
import { withSection } from "../../hoc";
import { responseUtil } from "../../utils";
import { useAssignGroups, useFetchGroups, useUpdateGroups } from "../../apiHooks";

import {
  FlexContainers,
  Heading4,
  PageContainer,
  Paragraph2,
  WrapperContainer
} from "../../components";
import { NewUserForm, UserGroupsModal } from "../../containers";

const CreateUserPage = ({ isAdminUser }) => {
  const loading = useSelector((store) => store.markets?.loading);
  const [isAppTypesLoading, setIsAppTypesLoading] = useState(false);
  const [groupSettingsModalActive, setGroupSettingsModalActive] = useState(false);

  const { mutationCallback: saveGroups } = useUpdateGroups();
  const { fetchCallback: fetchGroups, refresh } = useFetchGroups();
  const { mutationCallback: assignGroups } = useAssignGroups();

  const history = useHistory();

  const onCreateUser = async (user, groupAssignments) => {
    try {
      const res = isAdminUser
        ? await adminUserService.createAdminUser(user)
        : await usersService.createUser(user);

      await assignGroups(user.email, [], groupAssignments, isAdminUser);

      if (responseUtil.isResponseSuccessful(res)) {
        notificationService.showSuccess({
          title: t(strCode.CREATE_USER_SUCCESS),
          message: `${t(strCode.CREATE_USER_SUCCESS_INFO)} <b>${user.email}</b>`
        });

        dispatch(pushCreatedTableRowsIds([user.email]));

        dispatch(setUsersRefresh(true));
        history.push(routePaths.USERS);
      }
    } catch (err) {
      log.error("CreateUserPage: Failed to create user", err);
      notificationService.showError({
        title: t(strCode.CREATE_USER_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });

      throw err;
    }
  };

  const fetchMarkets = useCallback(async () => {
    dispatch(setMarketsError(null));

    if (loading) {
      return;
    }

    dispatch(setMarketsLoading(true));

    try {
      dispatch(resetMarkets());
      await marketsService.fetchMarkets();
    } catch (err) {
      dispatch(setMarketsError(err));

      log.error("CreateUserPage: Failed to fetch markets", err);
      notificationService.showError({
        title: t(strCode.FETCH_MARKETS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setMarketsLoading(false));
    }
  }, []);

  const fetchAppTypes = useCallback(async () => {
    setIsAppTypesLoading(true);

    try {
      await authService.setAppTypes();

      const appTypes = selectAppTypesGroups()(getState());

      await Promise.all(
        appTypes
          .filter((appType) => appType !== appTypesNames.allApps)
          .map((appType) => groupsService.fetchGroup(appType.appType))
      );
    } catch (err) {
      log.error("CreateUserPage: Failed to fetch apps", err);
      notificationService.showError({
        title: t(strCode.FETCH_APPS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      setIsAppTypesLoading(false);
    }
  }, []);

  useEffect(async () => {
    fetchMarkets();
    fetchAppTypes();
    fetchGroups();
  }, []);

  useEffect(() => {
    if (refresh) {
      fetchGroups(true);
    }
  }, [refresh]);

  return (
    <PageContainer alignItems={alignItems.start}>
      {loading || isAppTypesLoading ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
        </WrapperContainer>
      ) : (
        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          width="100%"
          height="100%">
          <Heading4>
            {t(isAdminUser ? strCode.CREATE_IAP_USER_TITLE : strCode.CREATE_USER_TITLE)}
          </Heading4>

          <NewUserForm
            isAdminUser={isAdminUser}
            onSaveUser={onCreateUser}
            setGroupSettingsModalActive={setGroupSettingsModalActive}
          />

          <UserGroupsModal
            isActive={groupSettingsModalActive}
            setActive={setGroupSettingsModalActive}
            onSubmit={saveGroups}
          />
        </WrapperContainer>
      )}
    </PageContainer>
  );
};

CreateUserPage.propTypes = {
  isAdminUser: PropTypes.bool
};

export default withSection(CreateUserPage);
